// Shared /api/v1 contract types.
//
// These describe the resources the versioned agent API operates on. Most of
// these entities (projects, brief versions, assets, compositions) are created
// and owned by the PR1-PR3 foundation; PR4 (timeline generation) only reads
// them and writes Jobs + VersionedTimelines. They live here as the agreed
// contract so every PR builds against the same shapes.

import { AspectRatio, CriticReport, ShotPlan, StoryContext, TimelineSegment } from "../types";

export type { AspectRatio } from "../types";

export const SCHEMA = {
  project: "project.v1",
  briefVersion: "brief.v1",
  asset: "asset.v1",
  composition: "composition.v1",
  timeline: "timeline.v1",
  job: "job.v1",
} as const;

// --- Project (PR1) ---------------------------------------------------------

export type ProjectStatus = "active" | "deleted";
export type ProjectVisibility = "public" | "private";

export interface V1Project {
  id: string;
  schemaVersion: typeof SCHEMA.project;
  workspaceId: string;
  name: string;
  // Stable, workspace-scoped, lowercase handle written by the generating agent.
  slug?: string | null;
  status: ProjectStatus;
  visibility?: ProjectVisibility;
  brief?: VideoBriefInput | null;
  currentBriefVersionId?: string | null;
  hasStoryboard?: boolean;
  // Movie-poster thumbnail: the project-scoped 'poster' selection (falling
  // back to the project's newest ready image). Signed, short-lived URL.
  posterAssetId?: string | null;
  posterUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type StoryboardStatus =
  | "draft"
  | "generating"
  | "ready"
  | "reviewing"
  | "approved"
  | "archived";

export type StoryboardItemStatus =
  | "draft"
  | "queued"
  | "generating"
  | "ready"
  | "approved"
  | "rejected"
  | "failed";

export interface StoryboardPanel {
  id: string;
  projectId: string;
  beatId: string;
  panelIndex: number;
  imageAssetId: string | null;
  promptAssetId: string | null;
  prompt?: string;
  url?: string;
  thumbnailUrl?: string;
  status: StoryboardItemStatus;
  isSelected: boolean;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StoryboardBeat {
  id: string;
  projectId: string;
  sceneId: string;
  beatIndex: number;
  intent: string;
  visualDescription: string | null;
  dialogueSummary: string | null;
  narration: string | null;
  durationSec: number | null;
  shotType: string | null;
  camera: string | null;
  framing: string | null;
  status: StoryboardItemStatus;
  beatAssetId: string | null;
  panels: StoryboardPanel[];
  createdAt: string;
  updatedAt: string;
}

export interface StoryboardScene {
  id: string;
  projectId: string;
  storyboardId: string;
  sceneIndex: number;
  title: string | null;
  summary: string | null;
  setting: string | null;
  mood: string | null;
  durationSec: number | null;
  sceneAssetId: string | null;
  // Resolved media for the scene's disposable cartoon wireframe (scene_asset_id).
  // Absent until one is generated — the UI then offers a Generate action.
  url?: string;
  thumbnailUrl?: string;
  status: StoryboardItemStatus;
  beats: StoryboardBeat[];
  createdAt: string;
  updatedAt: string;
}

export interface ProjectStoryboard {
  id: string;
  projectId: string;
  planAssetId: string | null;
  status: StoryboardStatus;
  scenes: StoryboardScene[];
  createdAt: string;
  updatedAt: string;
}

// --- Brief (PR1) -----------------------------------------------------------

export interface VideoBriefInput {
  goal: string;
  targetLengthSec: number;
  aspectRatio: AspectRatio;
  platform?: StoryContext["platform"];
  audience?: string;
  style?: string;
  format?: StoryContext["format"];
  hookQuestion?: string;
  strongestVisual?: string;
  oneBigIdea?: string;
  caveat?: string;
  payoff?: string;
  narration?: {
    mode: "none" | "generate" | "provided_text" | "provided_asset";
    script?: string;
    voiceId?: string;
    audioAssetId?: string;
  };
  constraints?: {
    mustUseAssetIds?: string[];
    avoidAssetIds?: string[];
    requiredBeats?: string[];
    forbiddenClaims?: string[];
    brandVoice?: string;
    callToAction?: string;
  };
}

export interface BriefVersion {
  id: string;
  schemaVersion: typeof SCHEMA.briefVersion;
  projectId: string;
  brief: VideoBriefInput;
  createdAt: string;
}

// --- Asset (PR1/PR2) -------------------------------------------------------

export type AssetKind = "video" | "image" | "audio";
export type AssetStatus = "pending" | "processing" | "ready" | "failed";
export type AssetMediaType = AssetKind | "text" | "reference";
export type AssetOrigin = "uploaded" | "generated" | "imported" | "derived";
export type AssetUse =
  | "primary_footage"
  | "b_roll"
  | "character_reference"
  | "style_reference"
  | "location_reference"
  | "logo_or_brand"
  | "music"
  | "voiceover"
  | "dialogue"
  | "sound_effect"
  | "title_or_graphic";
export type KnowledgeConfidence = "low" | "medium" | "high";
export type KnowledgeAction =
  | "ask_user"
  | "sample_video"
  | "analyze_image"
  | "transcribe_audio";

export interface UserAssetContext {
  title?: string;
  description?: string;
  people?: string[];
  characterNames?: string[];
  location?: string;
  event?: string;
  notableMoments?: string[];
  tags?: string[];
  transcriptHint?: string;
  audioNotes?: string;
  intendedUse?: AssetUse[];
  mustUse?: boolean;
  avoid?: boolean;
}

export type UserClipContext = UserAssetContext;

export interface UsableMoment {
  startSec: number;
  endSec: number;
  label: string;
  description: string;
  suggestedUse:
    | "hook"
    | "context"
    | "proof"
    | "emotion"
    | "transition"
    | "detail"
    | "b_roll"
    | "cta";
}

export interface AgentAssetContext {
  summary: string;
  mediaType: AssetMediaType;
  subjects: string[];
  actions?: string[];
  setting?: string;
  mood?: string;
  likelyUses: AssetUse[];
  cautions: string[];
  transcriptSummary?: string;
  confidence: KnowledgeConfidence;
  sampledAssetIds: string[];
  model: {
    provider: string;
    model?: string;
  };
}

export interface AgentClipContext extends AgentAssetContext {
  mediaType: "video";
  visualSubjects: string[];
  shotTypes: string[];
  usableMoments: UsableMoment[];
  sampledFrames: string[];
}

export interface KnowledgeGap {
  field: string;
  question: string;
  canInferAutomatically: boolean;
  suggestedAction: KnowledgeAction;
}

export interface KnownFact {
  field: string;
  value: string;
  confidence: KnowledgeConfidence;
  source: "user" | "agent" | "generation_prompt" | "metadata" | "transcript";
}

export interface AssetConstraint {
  type:
    | "must_use"
    | "avoid"
    | "likeness_reference"
    | "style_reference"
    | "brand_required"
    | "audio_required"
    | "no_audio"
    | "do_not_crop"
    | "do_not_modify";
  reason?: string;
}

export interface AssetRelationship {
  type:
    | "derived_from"
    | "sampled_from"
    | "represents_character"
    | "represents_location"
    | "belongs_to_scene"
    | "audio_for"
    | "visual_for";
  targetAssetId: string;
  description?: string;
}

export interface AssetKnowledge {
  assetId: string;
  mediaType: AssetMediaType;
  origin: AssetOrigin;
  userContext?: UserAssetContext;
  agentContext?: AgentAssetContext | AgentClipContext;
  knowledgeScore: number;
  knowledgeSummary: string;
  knownFacts: KnownFact[];
  unknowns: KnowledgeGap[];
  likelyUses: AssetUse[];
  constraints: AssetConstraint[];
  relationships: AssetRelationship[];
  provenance: {
    createdAt: string;
    updatedAt: string;
    analysisVersion: string;
    model?: {
      provider: string;
      model?: string;
    };
    sourcePrompt?: string;
    sampledAssetIds: string[];
    transcriptAssetId?: string;
  };
}

export interface ClipUnderstanding {
  assetId: string;
  source: "upload" | "generated";
  userContext?: UserClipContext;
  agentContext?: AgentClipContext | AgentAssetContext;
  combinedSummary: string;
  timelineHints: {
    mustUse: boolean;
    avoid: boolean;
    preferredBeats: string[];
    bestStartSec?: number;
    bestEndSec?: number;
  };
  provenance: {
    userContextUpdatedAt?: string;
    analyzedAt?: string;
    analysisVersion: string;
    sampledFrameAssetIds: string[];
  };
}

export interface AssetKnowledgeSummary {
  assetId: string;
  mediaType: AssetMediaType;
  known: string[];
  unknown: KnowledgeGap[];
  likelyUses: AssetUse[];
  confidence: KnowledgeConfidence;
}

export interface AssetInventoryReport {
  projectId: string;
  assets: AssetKnowledgeSummary[];
  globalKnowns: string[];
  globalUnknowns: KnowledgeGap[];
  recommendedLearningActions: {
    assetId?: string;
    action: KnowledgeAction;
    reason: string;
  }[];
  coverageEstimate: {
    video: "none" | "partial" | "complete";
    images: "none" | "partial" | "complete";
    audio: "none" | "partial" | "complete";
    characters: "none" | "partial" | "complete";
    brandsOrLogos: "none" | "partial" | "complete";
  };
}

export interface V1Asset {
  id: string;
  schemaVersion: typeof SCHEMA.asset;
  projectId: string;
  workspaceId: string;
  kind: AssetKind;
  status: AssetStatus;
  filename: string;
  url: string; // served/managed path the renderer can read
  thumbnailUrl?: string;
  // Null for stable public/remote media; ISO timestamp for private signed URLs.
  expiresAt?: string | null;
  durationSec: number;
  // Human display name written by the generating agent (falls back to a derived name).
  name?: string;
  // Stable, project-scoped, lowercase handle written by the generating agent. Agents
  // may reference this asset by (project, slug); resolved in the store's getAssetRow.
  slug?: string | null;
  description?: string;
  userContext?: UserAssetContext;
  agentContext?: AgentAssetContext | AgentClipContext;
  assetKnowledge?: AssetKnowledge;
  clipUnderstanding?: ClipUnderstanding;
  source: "upload" | "remote_url" | "local_path" | "generated" | "derived";
  // Set when this asset was produced by a PR2 generated-asset job.
  generatedAssetJobId?: string;
  createdAt: string;
  updatedAt: string;
}

// --- Composition (PR3) -----------------------------------------------------

export type CompositionMode = "asset_driven" | "prompt_only" | "hybrid";
export type CompositionStatus =
  | "planning"
  | "generating_assets"
  | "ready_for_timeline"
  | "failed";

export interface PlannedBeat {
  name: string;
  intent: string;
  durationSec: number;
  assetStrategy: "use_existing" | "generate_image" | "generate_video";
  requiredAssetIds?: string[];
  generatedAssetJobIds?: string[];
}

export interface CompositionPlan {
  id: string;
  schemaVersion: typeof SCHEMA.composition;
  projectId: string;
  briefVersionId: string;
  mode: CompositionMode;
  status: CompositionStatus;
  plannedBeats: PlannedBeat[];
  generatedAssetJobIds: string[];
  // Assets the composition resolved/produced that are ready for the timeline.
  readyAssetIds: string[];
  narrationStrategy?: {
    mode: "none" | "provided" | "generate";
    script?: string;
    audioAssetId?: string;
    estimatedDurationSec?: number;
    actualDurationSec?: number;
  };
  createdAt: string;
  updatedAt: string;
}

// --- Jobs ------------------------------------------------------------------

export type JobType =
  | "asset_ingest"
  | "asset_generation"
  | "asset_embedding"
  | "composition"
  | "generation"
  | "revision"
  | "export"
  | "audio_alignment";

export type JobStatus = "queued" | "running" | "succeeded" | "failed" | "canceled";

export interface JobProgress {
  currentStep?: string;
  // Wall-clock time the current step started. Used by operator diagnostics to
  // identify slow stages without a separate metrics pipeline.
  stepStartedAt?: string;
  percent?: number;
  message?: string;
  provider?: string;
  startedAt?: string;
  heartbeatAt?: string;
  lastProgressAt?: string;
  completedItems?: number;
  totalItems?: number;
  currentItem?: {
    id?: string;
    label: string;
    index?: number;
  };
  attempt?: number;
  nextRetryAt?: string;
}

export interface JobError {
  code: string;
  message: string;
}

export interface Job<TInput = unknown, TResult = unknown> {
  id: string;
  schemaVersion: typeof SCHEMA.job;
  workspaceId: string;
  projectId: string;
  // Correlation ID of the HTTP request that created the job. Logged on every
  // lifecycle event so a slow or failed job can be traced back to its request.
  requestId?: string;
  type: JobType;
  status: JobStatus;
  progress: JobProgress;
  input: TInput | null;
  result: TResult | null;
  error: JobError | null;
  /** Canonical action that initiated this job, when it came from a tool invocation. */
  actionId?: string;
  /**
   * The agent session's durable claim generation copied at launch when the
   * launching run held a domain-session claim. Finalization writes are fenced
   * against the session's current generation so a stale, reclaimed worker
   * cannot commit late.
   */
  sessionClaimGeneration?: number;
  idempotencyKey?: string;
  createdAt: string;
  updatedAt: string;
}

// --- Timeline (PR4 output) -------------------------------------------------

export interface TimelineProvenance {
  briefVersionId: string;
  compositionId?: string;
  sourceAssetIds: string[];
  generatedAssetJobIds: string[];
  agentClientId?: string;
  modelCallIds?: string[];
  criticReport: CriticReport | null;
  appliedPatchCount: number;
  // Populated by the PR5 audio_alignment step; recorded here for traceability.
  audioAlignment?: {
    decision: string;
    measuredAudioDurationSec?: number;
  };
}

export interface VersionedTimeline {
  id: string;
  schemaVersion: typeof SCHEMA.timeline;
  projectId: string;
  briefVersionId: string;
  compositionId?: string;
  aspectRatio: AspectRatio;
  fps: number;
  showCaptions?: boolean;
  segments: TimelineSegment[];
  provenance: TimelineProvenance;
  createdBy: { jobId: string };
  createdAt: string;
}

// --- Generation request/result --------------------------------------------

export interface GenerationRequest {
  briefVersionId?: string;
  assetIds?: string[];
  compositionId?: string;
  mode?: CompositionMode;
  allowGeneratedGapFill?: boolean;
  variantCount?: number;
  audioAlignment?: {
    mode: string;
    audioAssetId?: string;
  };
  showCaptions?: boolean;
}

// Validated, resolved inputs the executor runs against. Stored on the job so
// execution does not re-validate or re-resolve.
export interface GenerationJobInput {
  briefVersionId: string;
  compositionId?: string;
  mode?: CompositionMode;
  allowGeneratedGapFill?: boolean;
  assetIds: string[];
  generatedAssetJobIds: string[];
  variantCount: number;
  showCaptions?: boolean;
}

export interface GenerationJobResult {
  timelineIds: string[];
}

export type GenerationJob = Job<GenerationJobInput, GenerationJobResult>;


// --- Generation Runs (Progress UI) -----------------------------------------
//
// A GenerationRun is the run-level aggregate the progress UI renders against:
// one end-to-end video-generation attempt expressed as a sequence of stages.
//
// Run state maps onto existing job state — it does NOT introduce a second
// status vocabulary:
//   - GenerationRunStatus IS JobStatus. The same queued/running/succeeded/
//     failed/canceled states stay the source of truth. A run's status is
//     derived from the states of the jobs it aggregates:
//       * queued    - no underlying job has started running yet.
//       * running   - at least one underlying job is running.
//       * failed    - a non-retryable job failed and blocks the run.
//       * canceled  - the run (and its active jobs) were canceled.
//       * succeeded - the export job succeeded and the video is ready.
//   - GenerationStage and GenerationStageItem reuse the same status union.
//   - Per-job JobProgress (currentStep/percent/message) rolls up into the
//     run-level currentStageType/progressPercent/message and the matching
//     stage's progressPercent/message.
//   - jobIds and artifactIds point back to the authoritative Job and Artifact
//     records; the run never duplicates their state.
//   - Review checkpoints are represented by reviewGate, not by adding an
//     "awaiting_review" status. A paused run remains an active JobStatus
//     while execution is idle and waiting for approval.

export type GenerationRunStatus = JobStatus;

export type GenerationRunActivityState =
  | "working"
  | "waiting_on_job"
  | "waiting_on_domain"
  | "recovering";

// Ordered stage types a run can move through. Individual runs may skip stages
// they do not need (e.g. a prompt-only run with no uploaded assets).
export type GenerationStageType =
  | "brief_intake"
  | "creative_plan"
  | "script"
  | "storyboard"
  | "asset_generation"
  | "audio_generation"
  | "timeline_assembly"
  | "quality_review"
  | "export"
  | "ready";

// The user's per-run choice of which stages should pause for review after they
// complete. An empty or omitted list is a straight-through run.
export const GATEABLE_GENERATION_STAGE_TYPES = [
  "brief_intake",
  "creative_plan",
  "script",
  "storyboard",
  "asset_generation",
  "audio_generation",
  "timeline_assembly",
  "quality_review",
  "export",
] as const satisfies readonly GenerationStageType[];

export type GateableGenerationStageType =
  (typeof GATEABLE_GENERATION_STAGE_TYPES)[number];

export interface ReviewGateConfig {
  gatedStages: GateableGenerationStageType[];
}

// Run-level review state, orthogonal to GenerationRunStatus. When present, the
// run is awaiting user approval before the next stage can start.
export interface RunReviewGate {
  stageType: GateableGenerationStageType;
  stageId: string;
  state: "awaiting_review";
  enteredAt: string;
}

// User-safe error summary for a failed run, stage, or stage item. `code` and
// `message` mirror JobError; `retryable` and the redacted, diagnostic-safe
// `details` carry the extras the progress UI needs to offer recovery.
export interface GenerationErrorSummary {
  code: string;
  message: string;
  // Keep optional so existing fixture/demo payloads can omit non-essential metadata.
  retryable?: boolean;
  // Optional diagnostic detail suitable for UI copy or troubleshooting surfaces.
  details?: string;
}

export type GenerationJudgmentVerdict = "pass" | "needs_review" | "fail";

export interface GenerationJudgmentSummary {
  judgmentId: string;
  evaluatorId: string;
  verdict: GenerationJudgmentVerdict;
  rationale: string;
  recommendedAction?: "keep" | "regenerate" | "manual_review";
  grades?: Record<string, number | "pass" | "needs_review" | "fail">;
  evidenceRef?: string;
  createdAt: string;
}

export interface GenerationRun {
  runId: string;
  projectId: string;
  briefVersionId?: string;
  status: GenerationRunStatus;
  completionKind?: "video" | "storyboard_assets" | "standalone_asset" | "script";
  presentationKind?:
    | "standalone_image"
    | "standalone_video"
    | "standalone_audio"
    | "script_creation";
  /** Server-projected state of the mandatory storyboard review boundary. */
  storyboardBoundaryStatus?: "pending" | "reached" | "resolved";
  activityState?: GenerationRunActivityState;
  currentToolName?: string;
  reviewGates?: GateableGenerationStageType[];
  reviewGate?: RunReviewGate | null;
  reviewFeedback?: string | null;
  currentStageType?: GenerationStageType;
  progressPercent?: number;
  message?: string;
  lastProgressAt?: string;
  createdAt: string;
  updatedAt: string;
  startedAt?: string;
  completedAt?: string;
  error?: GenerationErrorSummary;
  judgment?: GenerationJudgmentSummary;
}

export type GenerationActivityAttentionState =
  | "normal"
  | "slow"
  | "possibly_stalled";

export interface GenerationJobActivity {
  status: JobStatus;
  currentStep?: string;
  providerLabel?: string;
  startedAt?: string;
  heartbeatAt?: string;
  lastProgressAt?: string;
  completedItems?: number;
  totalItems?: number;
  currentItemLabel?: string;
  attentionState: GenerationActivityAttentionState;
}

export interface GenerationJobDiagnostics extends GenerationJobActivity {
  jobId: string;
  actionId: string;
  runId: string;
  message?: string;
  provider?: string;
  attempt?: number;
  nextRetryAt?: string;
  updatedAt: string;
}

export interface GenerationStage {
  stageId: string;
  runId: string;
  type: GenerationStageType;
  toolName?: string;
  label: string;
  order: number;
  status: GenerationRunStatus;
  isReviewGate?: boolean;
  reviewedAt?: string | null;
  progressPercent?: number;
  message?: string;
  startedAt?: string;
  completedAt?: string;
  jobIds: string[];
  artifactIds: string[];
  createdAt: string;
  updatedAt: string;
  jobActivities?: GenerationJobActivity[];
  error?: GenerationErrorSummary;
  judgment?: GenerationJudgmentSummary;
}

// Child item of an asset-heavy stage so the UI can show per-beat cards.
export type GenerationStageItemKind =
  | "image"
  | "video"
  | "audio"
  | "caption"
  | "timeline"
  | "export";

export type GenerationStageItemPurpose =
  | "asset"
  | "brief"
  | "plan"
  | "storyboard_frame"
  | "visual_anchor"
  | "keyframe"
  | "shot"
  | "audio"
  | "caption"
  | "timeline"
  | "quality_review"
  | "export"
  | "unknown";

export interface GenerationStageItem {
  itemId: string;
  stageId: string;
  kind: GenerationStageItemKind;
  purpose: GenerationStageItemPurpose;
  label: string;
  status: GenerationRunStatus;
  progressPercent?: number;
  provider?: string;
  prompt?: string;
  promptPreview?: string;
  assetId?: string;
  artifactId?: string;
  retryable?: boolean;
  createdAt: string;
  updatedAt: string;
  error?: GenerationErrorSummary;
  judgment?: GenerationJudgmentSummary;
}

export interface BoardRevisionTarget {
  scope: "concept" | "brief" | "script" | "board" | "tile" | "asset";
  runId?: string;
  stageId?: string;
  itemId?: string;
  fieldId?: string;
  currentValue?: string;
  currentBrief?: VideoBriefInput;
  storyboardId?: string;
  sceneId?: string;
  beatId?: string;
  panelId?: string;
  keyframeAssetId?: string;
  clipAssetId?: string;
  assetId?: string;
  targetAssetUse?: AssetUse;
  artifactId?: string;
  label?: string;
}

// Canonical order and default labels for the stage rail. Individual runs may
// skip stages they do not need; the rail orders whatever it is given by this
// position.
export const GENERATION_STAGE_ORDER: Record<GenerationStageType, number> = {
  brief_intake: 0,
  creative_plan: 1,
  script: 2,
  storyboard: 3,
  asset_generation: 4,
  audio_generation: 5,
  timeline_assembly: 6,
  quality_review: 7,
  export: 8,
  ready: 9,
};

export const GENERATION_STAGE_LABELS: Record<GenerationStageType, string> = {
  brief_intake: "Brief",
  creative_plan: "Plan",
  script: "Script",
  storyboard: "Storyboard",
  asset_generation: "Visuals",
  audio_generation: "Audio",
  timeline_assembly: "Timeline",
  quality_review: "Review",
  export: "Render",
  ready: "Ready",
};
